import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Clock, Package, ArrowLeft, Loader2, Star, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/context/CartContext";
import { Tables } from "@/integrations/supabase/types";
import { transformTrip } from "@/lib/trips";
import { toast } from "sonner";
interface BookingItem {
  title: string;
  destination: string;
  duration: string;
  quantity: number;
  price: number;
  image?: string;
}

interface Booking {
  id: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_amount: number;
  currency: string;
  status: string;
  items: BookingItem[];
  created_at: string;
}

const Dashboard = () => {
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [trips, setTrips] = useState<Tables<"trips">[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        let bookingsData = null;
        let bookingsError = null;

        if (isAdmin) {
          // Admins can see all bookings
          const result = await supabase
        .from("bookings")
        .select("*")
        .order("created_at", { ascending: false });
          bookingsData = result.data;
          bookingsError = result.error;
        } else {
          // Regular users: fetch bookings by user_id OR customer_email
          // Make two queries and combine results to ensure we get all user bookings
          const [byUserIdResult, byEmailResult] = await Promise.all([
            supabase
              .from("bookings")
              .select("*")
              .eq("user_id", user.id)
              .order("created_at", { ascending: false }),
            user.email
              ? supabase
                  .from("bookings")
                  .select("*")
                  .eq("customer_email", user.email)
                  .order("created_at", { ascending: false })
              : { data: null, error: null },
          ]);

          // Combine results and remove duplicates
          const bookingsById = byUserIdResult.data || [];
          const bookingsByEmail = byEmailResult.data || [];
          const allBookings = [...bookingsById, ...bookingsByEmail];
          
          // Remove duplicates based on booking ID
          const uniqueBookings = allBookings.filter(
            (booking, index, self) =>
              index === self.findIndex((b) => b.id === booking.id)
          );
          
          // Sort by created_at descending
          uniqueBookings.sort(
            (a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );

          bookingsData = uniqueBookings;
          bookingsError = byUserIdResult.error || byEmailResult.error;
        }

      if (bookingsError) {
        console.error("Error fetching bookings:", bookingsError);
          console.error("User ID:", user.id);
          console.error("User Email:", user.email);
          toast.error("Failed to load bookings. Please try again.");
        } else {
          console.log("Fetched bookings:", bookingsData?.length || 0);
          if (bookingsData) {
        const formattedBookings = bookingsData.map((booking) => ({
          ...booking,
          items: (booking.items as unknown as BookingItem[]) || [],
          razorpay_order_id: booking.razorpay_order_id || "",
          razorpay_payment_id: booking.razorpay_payment_id || "",
        }));
        setBookings(formattedBookings);
          }
        }
      } catch (err) {
        console.error("Unexpected error fetching bookings:", err);
        toast.error("An unexpected error occurred. Please try again.");
      }

      // Fetch trips for overview
      const { data: tripsData, error: tripsError } = await supabase
        .from("trips")
        .select("*")
        .order("rating", { ascending: false })
        .limit(6);

      if (tripsError) {
        console.error("Error fetching trips:", tripsError);
      } else if (tripsData) {
        setTrips(tripsData);
      }

      setIsLoading(false);
    };

    if (user) {
      fetchData();
    }
  }, [user, isAdmin]);

  if (authLoading || isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-12 bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!user) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen pt-24 pb-12 bg-gradient-sunset">
        <div className="container mx-auto px-4">
          <Button variant="ghost" className="mb-6 gap-2" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </Button>

          <div className="mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
              {isAdmin ? "All Bookings" : "My Bookings"}
            </h1>
            <p className="text-muted-foreground">
              {isAdmin ? "View all customer bookings and their details" : "View all your trip bookings and their details"}
            </p>
          </div>

          {bookings.length === 0 ? (
            <Card className="border-0 shadow-card">
              <CardContent className="py-16 text-center">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="font-display text-xl font-semibold text-foreground mb-2">
                  No bookings yet
                </h2>
                <p className="text-muted-foreground mb-6">
                  Start exploring and book your first adventure!
                </p>
                <Button asChild>
                  <Link to="/">Browse Trips</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {bookings.map((booking) => (
                <Card key={booking.id} className="border-0 shadow-card overflow-hidden">
                  <CardHeader className="bg-muted/30">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <CardTitle className="font-display text-xl flex items-center gap-3">
                          Booking #{booking.id.slice(0, 8).toUpperCase()}
                          <Badge
                            variant={booking.status === "completed" ? "default" : "secondary"}
                            className={
                              booking.status === "completed"
                                ? "bg-green-500/10 text-green-600 border-green-500/20"
                                : booking.status === "pending_verification"
                                ? "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"
                                : ""
                            }
                          >
                            {booking.status}
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(booking.created_at)}
                        </div>
                        {isAdmin && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            <p><strong>Customer:</strong> {booking.customer_name}</p>
                            <p><strong>Email:</strong> {booking.customer_email}</p>
                            <p><strong>Phone:</strong> {booking.customer_phone}</p>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary">
                          ₹{booking.total_amount.toLocaleString("en-IN")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {booking.items.length} trip{booking.items.length > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {booking.items.map((item, index) => (
                        <div
                          key={index}
                          className="flex gap-4 pb-4 border-b border-border last:border-0 last:pb-0"
                        >
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground">{item.title}</h4>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {item.destination}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {item.duration}
                              </span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-sm text-muted-foreground">
                                Qty: {item.quantity}
                              </span>
                              <span className="font-semibold text-foreground">
                                ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {booking.razorpay_payment_id && (
                      <div className="mt-6 pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                          Payment ID: <span className="font-mono">{booking.razorpay_payment_id}</span>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Trips Overview Section */}
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-1">
                  Browse Trips
                </h2>
                <p className="text-muted-foreground">
                  Explore our top-rated adventures
                </p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/#trips">View All</Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trips.map((trip) => (
                <Card key={trip.id} className="border-0 shadow-card overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={trip.image}
                      alt={trip.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground">
                      {trip.category}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                      {trip.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {trip.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {trip.duration}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        {trip.rating}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {trip.group_size}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold text-primary">
                        ₹{trip.price.toLocaleString("en-IN")}
                      </p>
                      <Button
                        size="sm"
                        onClick={() => addToCart(transformTrip(trip))}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
